import React, { useEffect, useState, useCallback } from 'react';
import BackButton from '@/components/BackButton';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import useEmployeesMap from '@/hooks/useEmployeesMap';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Receipt, CheckCircle, XCircle, Plus, Trash, Download, Eye, ArrowLeft, ArrowRight, Airplane, Clock } from '@phosphor-icons/react';
import { toast } from 'sonner';
import ApprovalProgress, { clearanceProgressSteps } from '@/components/ApprovalProgress';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/branding/logo.png";

const STATUS_BADGE = {
  pending_admin: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  pending_finance: 'bg-orange-100 text-orange-800 border-orange-200',
  pending_gm: 'bg-purple-100 text-purple-800 border-purple-200',
  pending_employee_signature: 'bg-blue-100 text-blue-800 border-blue-200',
  signed: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  rejected: 'bg-red-100 text-red-800 border-red-200',
  cancelled_unsigned: 'bg-red-100 text-red-800 border-red-200',
};

const STATUS_AR = {
  pending_admin: 'بانتظار اعتماد مدير النظام',
  pending_finance: 'بانتظار المدير المالي',
  pending_gm: 'بانتظار المدير العام',
  pending_employee_signature: 'بانتظار توقيع الموظف',
  signed: 'موقّعة',
  rejected: 'مرفوضة',
  cancelled_unsigned: 'مُلغاة (لم تُوقّع)',
};

const STATUS_EN = {
  pending_admin: 'Awaiting System Admin',
  pending_finance: 'Awaiting Finance Manager',
  pending_gm: 'Awaiting General Manager',
  pending_employee_signature: 'Awaiting Employee Signature',
  signed: 'Signed',
  rejected: 'Rejected',
  cancelled_unsigned: 'Cancelled (unsigned)',
};

export default function AdminClearance({ embed = false }) {
  const { language, direction } = useLanguage();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { empNo } = useEmployeesMap();
  const isAdmin = user?.role === 'admin';
  const isGM = user?.role === 'general_manager' || !!user?.is_general_manager;
  const isFinanceMgr = !!user?.is_finance_manager;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // clearance being deleted
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [payoutDays, setPayoutDays] = useState(0);
  const [deductions, setDeductions] = useState([]);
  const [adminNotes, setAdminNotes] = useState('');
  const [signingName, setSigningName] = useState('');
  const [ticketAmount, setTicketAmount] = useState('');
  const [manualGross, setManualGross] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const T = (ar, en) => language === 'ar' ? ar : en;

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/clearance`, { withCredentials: true });
      setItems(data || []);
    } catch (e) {
      toast.error(T('تعذّر تحميل المخالصات', 'Failed to load clearances'));
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openItem = (it) => {
    setActive(it);
    const p = it.preview || {};
    // Default to suggested days (or leave_days) if not yet reviewed; admin
    // can lower it down to 0 — the allowance is at the employer's discretion.
    const defaultDays = it.final_payout_days != null
      ? it.final_payout_days
      : (p.suggested_payout_days ?? p.leave_days ?? 0);
    setPayoutDays(defaultDays);
    setDeductions(it.deductions || []);
    setAdminNotes(it.admin_notes || '');
    setSigningName(it.admin_reviewed_by_name || '');
    // Cash-only ticket: prefer the previously-set final amount, fall back to
    // whatever the employee requested.
    const tk = it.final_ticket_amount != null
      ? it.final_ticket_amount
      : (it.requested_ticket_amount ?? p.requested_ticket_amount ?? 0);
    setTicketAmount(tk || '');
    // Manual gross override — blank string means "use auto-computed value".
    setManualGross(it.manual_gross_override != null ? String(it.manual_gross_override) : '');
  };

  const totalDeductions = deductions.reduce((s, d) => s + (parseFloat(d.amount) || 0), 0);
  const daily = active?.preview?.daily_full_wage || 0;
  // Business rule (2026-02, reaffirmed 2026-07-17):
  //   Leave cash-outs are ALWAYS paid at the CURRENT daily wage,
  //   regardless of how old the accrued days are. This matches the
  //   backend's `admin_review` logic exactly, so the amount the
  //   reviewer SEES in the dialog is the exact amount that will be
  //   locked when they click Save/Approve. No more "displayed one
  //   value, saved a different value" surprises when historic FIFO
  //   slices exist at older lower salaries.
  //
  //   The FIFO breakdown is still surfaced elsewhere (audit trail /
  //   preview notes) but does NOT drive the payout amount.
  const fifoSlices = active?.preview?.historic_cost?.slices_used || [];
  const requestedDays = parseFloat(payoutDays) || 0;
  const computedDaysGross = +(daily * requestedDays).toFixed(2);
  const isCashOnly = active?.type === 'cash_only';
  // Manual override for the days-value (before ticket & deductions). When
  // the reviewer types a value, it replaces the auto-computed
  // `days × daily_wage` figure. When blank, we fall back to the FIFO/
  // current-wage auto-calculation. This value flows into the locked
  // snapshot on save, so every subsequent reader (employee, PDF, admin
  // list) sees exactly the number the reviewer typed.
  const manualGrossNum = manualGross === '' || manualGross == null
    ? null
    : (isNaN(parseFloat(manualGross)) ? null : parseFloat(manualGross));
  const effectiveDaysGross = manualGrossNum != null ? manualGrossNum : computedDaysGross;
  // Air-ticket eligibility rule:
  //   A cash-only request is only accepted when the employee's TRUE
  //   balance is ≥ 40 (CASH_ONLY_MIN_BALANCE). Since that already implies
  //   balance > 30, every ACCEPTED cash-only request is ticket-eligible.
  //
  //   Historical bug: the client used to compare `max_cashable_days` (the
  //   per-request CAP, hard-limited to 30) against 30 — so the check
  //   `maxCashable > 30` was NEVER true and the Finance Manager never saw
  //   the ticket-value input. We now compare `true_balance` directly.
  //
  //   `max_cashable_days` is still the CAP the Finance Manager can pay
  //   out (rendered as "الرصيد القابل للصرف"), but eligibility is a
  //   different question and must use `true_balance`.
  const trueBalance = active?.preview?.true_balance ?? 0;
  const maxCashable = active?.preview?.max_cashable_days ?? active?.preview?.remaining_days ?? 0;
  const ticketRequested = !!active?.ticket_requested;
  const ticketEligible = isCashOnly && trueBalance > 30;
  const ticketAmt = ticketEligible ? (parseFloat(ticketAmount) || 0) : 0;
  const computedGross = +(effectiveDaysGross + ticketAmt).toFixed(2);
  const computedNet = +(computedGross - totalDeductions).toFixed(2);
  // Has historic salary changes? Surface a note so the admin understands
  // why "days × current daily wage" may differ from the displayed value.
  const hasFifoBreakdown = fifoSlices.length > 0 && fifoSlices.some((s) => Math.abs((s.daily_rate || 0) - daily) > 0.01);

  const addDeduction = () => setDeductions([...deductions, { label: '', amount: 0 }]);
  const updateDeduction = (i, field, val) => {
    const next = [...deductions];
    next[i] = { ...next[i], [field]: field === 'amount' ? parseFloat(val) || 0 : val };
    setDeductions(next);
  };
  const removeDeduction = (i) => setDeductions(deductions.filter((_, idx) => idx !== i));

  // Whole-Riyal formatter — HR policy is to display clearance amounts
  // rounded to the nearest integer Riyal (no fils/decimals). Internal
  // calculations keep the raw value; only the DISPLAY is rounded.
  const fmtSar = (n) => Math.round(Number(n) || 0).toLocaleString();

  // ── SINGLE SOURCE OF TRUTH for displayed values ─────────────────
  // Once a reviewer has saved (locked_final_net is set on the doc), the
  // Admin dashboard, employee dialog, and PDF must ALL show the exact
  // same figure. So while the current reviewer is still editing (they
  // are actively changing days / deductions / ticket) we show the LIVE
  // recomputation as a preview — but the moment the doc is locked and
  // the current user is NOT the editor of this stage, we display the
  // frozen locked snapshot.

  // Note: we intentionally always show the LIVE `computedGross` /
  // `computedNet` (daily × days) instead of a legacy FIFO-historic
  // `final_net` from the DB. Business rule change (2026-02): leave
  // cash-outs are always paid at the CURRENT daily wage. Old clearances
  // whose stored `final_net` was computed under the historic formula
  // will show a live-recomputed figure here that may differ from the
  // list column until the admin re-saves the review.
  const staleSavedFinal =
    active?.final_net != null &&
    Math.abs(active.final_net - computedNet) > 1;

  const submitReview = async () => {
    if (!active) return;
    const days = parseFloat(payoutDays) || 0;
    if (days < 0) {
      toast.error(T('عدد الأيام يجب أن يكون 0 أو أكثر',
                    'Days must be 0 or more'));
      return;
    }
    setSubmitting(true);
    try {
      const prevStatus = active.status;
      await axios.put(
        `${API}/clearance/${active.id}/admin-review`,
        {
          final_payout_days: parseFloat(payoutDays) || 0,
          deductions: deductions.filter(d => d.label && d.amount > 0).map(d => ({ label: d.label, amount: parseFloat(d.amount) })),
          admin_notes: adminNotes,
          signing_name: signingName.trim() || null,
          final_ticket_amount: ticketEligible ? (parseFloat(ticketAmount) || 0) : null,
          manual_gross_override: manualGrossNum,
        },
        { withCredentials: true }
      );
      // Toast reflects the NEXT stage the doc moved to, not a fixed string.
      const successMsg = prevStatus === 'pending_admin'
        ? T('تم اعتماد المخالصة وإرسالها للمدير المالي', 'Clearance approved and sent to Finance Manager')
        : prevStatus === 'pending_employee_signature'
        ? T('تم حفظ التعديلات', 'Changes saved')
        : T('تم اعتماد المخالصة وإرسالها للموظف للتوقيع', 'Clearance approved and sent to employee for signature');
      toast.success(successMsg);
      setActive(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setSubmitting(false);
    }
  };

  // Decide which actions the current user can perform on the active item
  // Workflow (2026-07-17):
  //   • System Admin at `pending_admin`   → edits AMOUNT (deductions
  //     + notes) only. Cannot touch days or ticket.
  //   • Finance Manager at `pending_finance` → edits AMOUNT (deductions),
  //     DAYS count, and TICKET amount (cash-only). Full power.
  const canEditAmount = (
    (active?.status === 'pending_admin' && isAdmin) ||
    (active?.status === 'pending_finance' && (isFinanceMgr || isAdmin)) ||
    (active?.status === 'pending_gm' && (isGM || isAdmin))
  );
  const canEditDays = (
    (active?.status === 'pending_finance' && (isFinanceMgr || isAdmin)) ||
    (active?.status === 'pending_gm' && (isGM || isAdmin))
  );
  // Ticket amount is Finance Manager's responsibility (cash-only clearances).
  const canEditTicket = canEditDays;
  // Retain `canSetDays` name for downstream JSX that already references it —
  // now means "can save this stage" (either amount or days).
  const canSetDays = canEditAmount || canEditDays;
  const canReject = (
    (active?.status === 'pending_admin' && isAdmin) ||
    (active?.status === 'pending_finance' && (isFinanceMgr || isAdmin)) ||
    (active?.status === 'pending_gm' && (isGM || isAdmin))
  );

  // Display values — see "SINGLE SOURCE OF TRUTH" comment above. When
  // the doc is locked AND the current user isn't editing this stage,
  // show the frozen locked snapshot. Otherwise show the live preview.
  const hasLocked = active?.locked_final_net != null;
  const showLocked = hasLocked && !canSetDays;
  const displayDaysGross = showLocked
    ? Number(active.locked_final_gross_days ?? effectiveDaysGross)
    : effectiveDaysGross;
  const displayGross = showLocked
    ? Number(active.locked_final_gross ?? computedGross)
    : computedGross;
  const displayNet = showLocked
    ? Number(active.locked_final_net ?? computedNet)
    : computedNet;
  const displayTicketAmt = showLocked
    ? Number(active.locked_final_ticket_amount ?? ticketAmt)
    : ticketAmt;

  const rejectItem = async () => {
    if (!active) return;
    if (!window.confirm(T('هل تريد رفض هذه المخالصة؟', 'Reject this clearance?'))) return;
    try {
      await axios.post(
        `${API}/clearance/${active.id}/reject`,
        { final_payout_days: 0, deductions: [], admin_notes: adminNotes },
        { withCredentials: true }
      );
      toast.success(T('تم الرفض', 'Rejected'));
      setActive(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const downloadPdf = (id) => window.open(`${API}/clearance/${id}/pdf`, '_blank');

  // Body content is the same in both modes; only the outer chrome (header
  // + BackButton + page heading) differs when embedded inside the Leaves
  // page.
  const body = (
    <>
        <Card className="border border-[#C4BFF0]">
          <CardHeader>
            <CardTitle className="text-[#655dc6] text-lg">{T('جميع طلبات المخالصة', 'All Clearance Requests')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#C4BFF0]">
                    <TableHead className="text-start">{T('الرقم الوظيفي', 'Emp #')}</TableHead>
                    <TableHead className="text-start">{T('الموظف', 'Employee')}</TableHead>
                    <TableHead className="text-start">{T('فترة الإجازة', 'Leave Period')}</TableHead>
                    <TableHead className="text-start">{T('تاريخ الطلب', 'Requested')}</TableHead>
                    <TableHead className="text-start">{T('الحالة', 'Status')}</TableHead>
                    <TableHead className="text-start">{T('الصافي', 'Net')}</TableHead>
                    <TableHead className="text-start">{T('إجراء', 'Action')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-8 text-[#655dc6]/70">...</TableCell></TableRow>
                  ) : items.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-8 text-[#655dc6]/70">{T('لا توجد طلبات مخالصة حتى الآن', 'No clearance requests yet')}</TableCell></TableRow>
                  ) : items.map((it) => (
                    <TableRow key={it.id} className="border-[#C4BFF0]">
                      <TableCell className="text-[#655dc6]/80 font-mono text-xs">{empNo(it.employee_id)}</TableCell><TableCell className="font-medium text-[#655dc6]">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span>{it.preview?.employee_name || '-'}</span>
                          {it.type === 'cash_only' && (
                            <Badge className="bg-violet-100 text-violet-800 border-violet-200 text-[10px] px-1.5 py-0">
                              {language === 'ar' ? 'صرف نقدي' : 'Cash-only'}
                            </Badge>
                          )}
                          {/* Air-ticket-requested indicator — surfaces the
                              employee's ticket opt-in on the list level so
                              Finance can triage without opening each row. */}
                          {it.type === 'cash_only' && it.ticket_requested && (
                            <Badge
                              className="bg-blue-100 text-blue-800 border-blue-300 text-[10px] px-1.5 py-0 gap-1"
                              title={language === 'ar' ? 'الموظف طلب قيمة تذكرة الطيران' : 'Employee requested air-ticket value'}
                              data-testid={`ticket-req-badge-${it.id}`}
                            >
                              <Airplane size={11} weight="duotone" />
                              {language === 'ar' ? 'طلب تذكرة' : 'Ticket'}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-[#655dc6]/70 text-sm">
                        {it.type === 'cash_only'
                          ? (language === 'ar'
                              ? `${it.requested_days || it.preview?.requested_days || 0} يوم نقدي`
                              : `${it.requested_days || it.preview?.requested_days || 0} cash days`)
                          : `${it.preview?.leave_start} → ${it.preview?.leave_end}`}
                      </TableCell>
                      <TableCell className="text-[#655dc6]/70 text-sm">{(it.created_at || '').slice(0, 10)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Badge className={STATUS_BADGE[it.status] || ''}>
                            {language === 'ar' ? (STATUS_AR[it.status] || it.status) : (STATUS_EN[it.status] || it.status)}
                          </Badge>
                          {it.status === 'pending_employee_signature' && it.days_remaining_for_signature != null && (
                            <span
                              className={`text-[10px] px-1.5 py-0.5 rounded font-semibold whitespace-nowrap ${
                                it.days_remaining_for_signature <= 2
                                  ? 'bg-red-100 text-red-700 border border-red-300 animate-pulse'
                                  : 'bg-amber-100 text-amber-800 border border-amber-200'
                              }`}
                              title={language === 'ar'
                                ? 'يجب توقيع الموظف خلال 10 أيام من اعتماد الإدارة وإلا تُلغى تلقائياً'
                                : 'Auto-cancelled if not signed within 10 days of approval'}
                              data-testid={`adm-sign-countdown-${it.id}`}
                            >
                              ⏳ {it.days_remaining_for_signature > 0
                                ? (language === 'ar' ? `متبقي ${it.days_remaining_for_signature} يوم` : `${it.days_remaining_for_signature}d left`)
                                : (language === 'ar' ? `متبقي ${it.hours_remaining_for_signature || 0} ساعة` : `${it.hours_remaining_for_signature || 0}h left`)}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-bold text-[#655dc6]" data-testid={`list-net-${it.id}`}>
                        {(() => {
                          // SINGLE SOURCE OF TRUTH — once the clearance
                          // has been reviewed and locked, always show the
                          // frozen `locked_final_net` so the admin list,
                          // detail dialog, employee view and PDF all
                          // display the exact same figure. Only fall back
                          // to a live FIFO recomputation for docs that
                          // haven't been reviewed yet (no locked snapshot
                          // and no `final_net` saved).
                          if (it.locked_final_net != null) {
                            return `${fmtSar(it.locked_final_net)} SAR`;
                          }
                          if (it.final_net != null) {
                            return `${fmtSar(it.final_net)} SAR`;
                          }
                          // Business rule: leave cash-outs are paid at the
                          // CURRENT daily wage — same formula the backend
                          // uses on save. Do NOT use FIFO historic slices
                          // here; that would produce a lower figure and
                          // trigger a jump when the reviewer clicks Save.
                          const daily = it?.preview?.daily_full_wage || 0;
                          const days =
                            it.final_payout_days != null
                              ? it.final_payout_days
                              : (it.requested_days ?? it.preview?.requested_days ?? it.preview?.leave_days ?? 0);
                          if (days > 0 && daily > 0) {
                            const deductionsTotal = (it.final_deductions || []).reduce(
                              (a, d) => a + (Number(d.amount) || 0),
                              0,
                            );
                            const net = daily * days - deductionsTotal;
                            return `${fmtSar(net)} SAR`;
                          }
                          return '-';
                        })()}
                      </TableCell>
                      <TableCell className="flex gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openItem(it)}
                          className="h-7 text-xs gap-1 border-[#655dc6] text-[#655dc6] hover:bg-[#E8E6F5] hover:text-[#544cb0]"
                          data-testid={`open-clearance-${it.id}`}
                        >
                          <Eye size={14} weight="duotone" />
                          {(it.status === 'pending_admin' || it.status === 'pending_finance' || it.status === 'pending_gm') ? T('مراجعة', 'Review') : T('عرض', 'View')}
                        </Button>
                        {it.status === 'signed' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs gap-1 border-emerald-400 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
                            onClick={() => downloadPdf(it.id)}
                            data-testid={`download-pdf-${it.id}`}
                          >
                            <Download size={14} weight="duotone" />
                            PDF
                          </Button>
                        )}
                        {(isAdmin || isGM) && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs gap-1 border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
                            onClick={() => setDeleteTarget(it)}
                            data-testid={`delete-clearance-${it.id}`}
                            title={T('حذف المخالصة', 'Delete clearance')}
                          >
                            <Trash size={14} weight="duotone" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Review / View Dialog */}
        <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
          <DialogContent className="sm:max-w-[700px] max-h-[96vh] flex flex-col p-0" dir={direction}>
            <DialogHeader className="pb-1 px-6 pt-4 shrink-0">
              <DialogTitle className="flex items-center gap-2 text-[#655dc6] text-base">
                <Receipt size={20} weight="duotone" />
                {T('تفاصيل ومراجعة المخالصة', 'Clearance Details & Review')}
              </DialogTitle>
              <DialogDescription className="text-xs">
                {active && active.preview && (
                  <>
                    <strong>{active.preview.employee_name}</strong> · {active.preview.department || '-'} · {active.preview.position || '-'}
                  </>
                )}
              </DialogDescription>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto px-6">
            {active && active.preview && (
              <div className="space-y-2 text-xs" data-testid="clearance-review-grid">
                {/* Approval progress */}
                <div className="px-2 py-2 bg-white border border-[#C4BFF0] rounded">
                  <ApprovalProgress steps={clearanceProgressSteps(active)} />
                </div>

                {/* Leave / Cash-only info */}
                {isCashOnly ? (
                  <div className="p-2 bg-violet-50 border border-violet-200 rounded space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className="bg-violet-100 text-violet-800 border-violet-200 text-[10px]">
                        {ticketRequested
                          ? T('صرف نقدي مع طلب تذكرة', 'Cash-only · ticket requested')
                          : T('صرف نقدي بدون سفر', 'Cash-only · no travel')}
                      </Badge>
                      {ticketRequested && (
                        <Badge className="bg-blue-100 text-blue-800 border-blue-300 text-[10px] gap-1">
                          <Airplane size={10} weight="duotone" />
                          {T('طلب تذكرة', 'Ticket requested')}
                        </Badge>
                      )}
                      <span className="text-[10px] text-violet-700/80">
                        {T('سنة', 'Year')}: {active.request_year || active.preview?.request_year || '-'}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mt-1">
                      <div>
                        <div className="text-[10px] text-violet-700/70">{T('الأيام المطلوبة', 'Requested Days')}</div>
                        <div className="font-medium">{active.requested_days ?? active.preview?.requested_days ?? 0}</div>
                      </div>
                      <div>
                        <div className="text-[10px] text-violet-700/70">{T('الرصيد الفعلي', 'True Balance')}</div>
                        <div className="font-medium">{trueBalance} {T('يوم', 'days')}</div>
                      </div>
                      <div>
                        <div className="text-[10px] text-violet-700/70">{T('الحد الأقصى للصرف', 'Max Cashable')}</div>
                        <div className="font-medium">{maxCashable} {T('يوم', 'days')}</div>
                      </div>
                    </div>
                    <p className="text-[10px] text-violet-700/70 leading-snug">
                      {ticketEligible
                        ? T(
                            'يذهب الطلب مباشرة للمدير المالي. الرصيد الفعلي > 30 يوم — يمكنك إضافة قيمة تذكرة الطيران. الأيام تُخصم من رصيد الموظف فور التوقيع.',
                            'Routed directly to Finance Manager. True balance > 30 d — you may add an air-ticket value. Days deducted on signature.'
                          )
                        : T(
                            'يذهب الطلب مباشرة للمدير المالي. الرصيد الفعلي ≤ 30 يوم — لا تُضاف تذكرة الطيران لهذا الطلب. الأيام تُخصم من رصيد الموظف فور التوقيع.',
                            'Routed directly to Finance Manager. True balance ≤ 30 d — no air-ticket eligible. Days deducted on signature.'
                          )}
                    </p>
                  </div>
                ) : (
                  <div className="p-2 bg-[#E8E6F5] rounded space-y-1">
                    <div className="grid grid-cols-3 gap-2">
                      <div><div className="text-[10px] text-[#655dc6]/70">{T('بداية', 'Start')}</div><div className="font-medium">{active.preview.leave_start}</div></div>
                      <div><div className="text-[10px] text-[#655dc6]/70">{T('نهاية', 'End')}</div><div className="font-medium">{active.preview.leave_end}</div></div>
                      <div>
                        <div className="text-[10px] text-[#655dc6]/70">{T('عدد أيام الإجازة', 'Leave Days')}</div>
                        <div className="font-medium">{active.preview.leave_days}</div>
                      </div>
                    </div>
                    {/*
                      Forward-looking balance note (shown when the leave starts
                      in the future and the projected accrual actually adds
                      days). Explains why `max_cashable_days` may exceed
                      what today's snapshot suggests — the Finance Manager
                      may approve up to the balance the employee WILL have
                      by the leave start date.
                    */}
                    {(active.preview.projected_extra_days_by_leave_start ?? 0) > 0 && (
                      <div
                        className="flex items-center justify-between rounded-md bg-emerald-50 border border-emerald-200 px-2 py-1 text-[11px] text-emerald-800"
                        data-testid="projected-balance-note"
                      >
                        <span>
                          {T(
                            `الرصيد المتاح اليوم: ${active.preview.available_balance_today ?? active.preview.annual_entitlement_today ?? 0} يوم — يُضاف حتى بداية الإجازة`,
                            `Available today: ${active.preview.available_balance_today ?? active.preview.annual_entitlement_today ?? 0} d — accrues by leave start`
                          )}
                        </span>
                        <span className="font-mono font-semibold">
                          +{active.preview.projected_extra_days_by_leave_start} {T('يوم', 'd')}
                        </span>
                      </div>
                    )}
                    <p className="text-[10px] text-[#655dc6]/70 leading-snug">
                      {T(
                        'تُحسب الإجازة السنوية تقويمياً وفق نظام العمل السعودي (المادة 109) — تشمل أيام الراحة الأسبوعية والأعياد الرسمية.',
                        'Annual leave counted as calendar days per Saudi Labor Law Art. 109 — includes weekly rest days & holidays.'
                      )}
                    </p>
                  </div>
                )}

                {/* Read-only banner once the doc is locked for employee signature. */}
                {active.status === 'pending_employee_signature' && (
                  <div
                    className="rounded-md border border-blue-200 bg-blue-50 text-blue-900 px-3 py-2 flex items-center gap-2"
                    data-testid="awaiting-signature-banner"
                  >
                    <Clock size={16} weight="duotone" className="shrink-0" />
                    <div className="text-xs leading-snug">
                      <p className="font-semibold">
                        {T('بانتظار توقيع الموظف', 'Awaiting employee signature')}
                      </p>
                      <p className="opacity-80">
                        {T(
                          'المبلغ والخصومات مُقفلة بعد إرسال الطلب للتوقيع. للتعديل، يجب استرجاع الطلب من مرحلة المراجعة السابقة.',
                          'Amount and deductions are locked once sent for signature. To edit, roll the request back to the previous review stage.'
                        )}
                      </p>
                    </div>
                  </div>
                )}

                {/* Calculation preview */}
                <div className="p-2 border border-[#C4BFF0] rounded space-y-0.5">
                  <div className="flex justify-between"><span>{T('الراتب الشامل', 'Full Salary')}</span><span className="font-medium">{(active.preview.full_monthly_salary || 0).toLocaleString()} SAR</span></div>
                  <div className="flex justify-between">
                    <span>{T('الأجر اليومي (الحالي)', 'Daily Wage (current)')}</span>
                    <span className="font-medium">{(active.preview.daily_full_wage || 0).toLocaleString()} SAR</span>
                  </div>
                  {hasFifoBreakdown && (
                    <div className="flex justify-between text-amber-700 bg-amber-50 px-1 rounded">
                      <span title={T('متوسط مرجح حسب رواتب السنوات السابقة (FIFO)', 'Weighted avg by historic salaries (FIFO)')}>
                        {T('الأجر اليومي (FIFO)', 'Daily Wage (FIFO)')}
                      </span>
                      <span className="font-medium">
                        {(active.preview.historic_cost?.weighted_avg_daily_rate || 0).toLocaleString()} SAR
                      </span>
                    </div>
                  )}
                  {(active.preview.used_days_other_leaves ?? 0) > 0 && (
                    <div className="flex justify-between text-red-600"><span>{T('− إجازات أخرى هذا العام', '− Other leaves this year')}</span><span>{active.preview.used_days_other_leaves}</span></div>
                  )}
                  {!isCashOnly && (
                    <div className="flex justify-between text-blue-700 pt-0.5 border-t border-blue-200"><span className="font-semibold">{T('أيام الإجازة (المقترح)', 'Leave Days (suggested)')}</span><span className="font-bold">{active.preview.leave_days} {T('يوم', 'd')}</span></div>
                  )}
                </div>

                {/* Editable: payout days — no upper cap. The previously-shown
                    "max cashable" line and validation were removed per
                    business rule: payout follows the days the employee
                    actually requested, not an internal accrual cap. */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[11px]">
                      {T('عدد أيام الصرف', 'Payout Days')}
                    </Label>
                    <Input
                      type="number"
                      min={0}
                      step="0.5"
                      value={payoutDays}
                      onChange={(e) => setPayoutDays(e.target.value)}
                      disabled={!canEditDays}
                      className="h-8 text-xs"
                      data-testid="admin-payout-days-input"
                    />
                  </div>
                  <div className="p-1.5 bg-emerald-50 rounded text-center flex flex-col justify-center">
                    <div className="text-[10px] text-emerald-700/70">
                      {ticketEligible
                        ? T('قيمة الأيام', 'Days Value')
                        : T('إجمالي البدل', 'Gross')}
                    </div>
                    <div
                      className="text-sm font-bold text-emerald-700"
                      data-testid="clearance-gross-value"
                    >
                      {ticketEligible
                        ? fmtSar(displayDaysGross)
                        : fmtSar(displayGross)} SAR
                    </div>
                  </div>
                </div>

                {/* Manual gross-allowance override.
                    Lets the reviewer type the clearance amount directly
                    (before ticket & deductions) — replaces the auto-
                    computed `days × daily_wage` figure. Blank = use the
                    system's calculation. This value flows into the
                    locked snapshot on save and is what the employee,
                    admin list and PDF all display. */}
                <div className="rounded border border-[#655dc6]/30 bg-[#F7F5FE] p-2 space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <Label className="text-[11px] font-semibold text-[#655dc6]">
                      {T('تعديل يدوي لإجمالي البدل', 'Manual Gross Override')}
                    </Label>
                    {manualGross !== '' && canEditAmount && (
                      <button
                        type="button"
                        onClick={() => setManualGross('')}
                        className="text-[10px] text-[#655dc6] underline hover:text-[#544cb0]"
                        data-testid="clear-manual-gross-btn"
                      >
                        {T('إعادة للحساب التلقائي', 'Reset to auto')}
                      </button>
                    )}
                  </div>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    inputMode="decimal"
                    value={manualGross}
                    onChange={(e) => setManualGross(e.target.value)}
                    disabled={!canEditAmount}
                    placeholder={T(
                      `اترك فارغاً لاستخدام ${fmtSar(computedDaysGross)} ر.س (المحسوب تلقائياً)`,
                      `Leave blank to use ${fmtSar(computedDaysGross)} SAR (auto)`
                    )}
                    className="h-8 text-xs"
                    data-testid="manual-gross-override-input"
                  />
                  <p className="text-[9px] text-[#655dc6]/70">
                    {manualGrossNum != null
                      ? T(
                          `سيتم اعتماد هذا المبلغ بدل الحساب التلقائي (${fmtSar(computedDaysGross)} ر.س) ويظهر للكل بعد الحفظ.`,
                          `This value overrides the auto-computed ${fmtSar(computedDaysGross)} SAR and will be displayed everywhere after save.`
                        )
                      : T(
                          'اتركه فارغاً ليتم اعتماد المبلغ المحسوب تلقائياً من عدد الأيام × الأجر اليومي.',
                          'Leave blank to accept the auto-computed amount (days × daily wage).'
                        )}
                  </p>
                </div>

                {/* Cash-only: Finance Manager sets the actual air-ticket amount.
                    Only available when employee's cashable balance > 30 days. */}
                {ticketEligible && (
                  <div className="space-y-2">
                    {/* Employee opt-in indicator — surfaces whether the
                        worker explicitly asked for the air-ticket. */}
                    {active.ticket_requested ? (
                      <div className="flex items-start gap-2 p-1.5 rounded bg-blue-100 border border-blue-300 text-[11px] text-blue-900">
                        <Airplane size={14} weight="duotone" className="text-blue-700 shrink-0 mt-0.5" />
                        <span>{T('الموظف طلب الحصول على قيمة تذكرة الطيران مع هذا الصرف.',
                                'Employee requested an air-ticket value with this cash-out.')}</span>
                      </div>
                    ) : (
                      <div className="flex items-start gap-2 p-1.5 rounded bg-stone-100 border border-stone-300 text-[11px] text-stone-700">
                        <Airplane size={14} weight="duotone" className="text-stone-500 shrink-0 mt-0.5" />
                        <span>{T('الموظف لم يطلب تذكرة طيران — يمكنك تركها صفراً أو إضافتها تقديراً.',
                                'Employee did not request a ticket — leave 0 or grant as goodwill.')}</span>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[11px]">
                          {T('قيمة تذكرة الطيران المعتمدة', 'Approved Air-Ticket Value')}
                        </Label>
                        <Input
                          type="number" min="0" step="50"
                          value={ticketAmount}
                          onChange={(e) => setTicketAmount(e.target.value)}
                          disabled={!canEditTicket}
                          className="h-8 text-xs"
                          placeholder="0"
                          data-testid="admin-ticket-amount-input"
                        />
                        <p className="text-[9px] text-[#655dc6]/60 mt-0.5">
                          {T('قيمة التذكرة يحددها المدير المالي.',
                             'Ticket value is set by the Finance Manager.')}
                        </p>
                      </div>
                      <div className="p-1.5 bg-blue-50 rounded text-center flex flex-col justify-center">
                        <div className="text-[10px] text-blue-700/70">{T('الإجمالي مع التذكرة', 'Total with Ticket')}</div>
                        <div className="text-sm font-bold text-blue-700">{fmtSar(displayGross)} SAR</div>
                        <div className="text-[10px] text-blue-700/70 mt-0.5">
                          {fmtSar(displayDaysGross)} + {fmtSar(displayTicketAmt)} ({T('تذكرة', 'ticket')})
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Deductions */}
                <div className="border border-red-200 rounded p-2 space-y-1 bg-red-50/40">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-red-700">{T('الخصومات', 'Deductions')}</span>
                    <Button size="sm" variant="outline" className="h-6 text-[11px] gap-1 border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
                      onClick={addDeduction}
                      disabled={!canEditAmount}
                      data-testid="add-deduction-btn">
                      <Plus size={12} />
                      {T('إضافة', 'Add')}
                    </Button>
                  </div>
                  {deductions.length === 0 && <p className="text-[10px] text-red-700/60 text-center">{T('لا توجد خصومات', 'No deductions')}</p>}
                  {deductions.map((d, i) => (
                    <div key={i} className="grid grid-cols-[1fr_110px_28px] gap-1.5 items-center">
                      <Input
                        placeholder={T('وصف', 'Label')}
                        value={d.label}
                        onChange={(e) => updateDeduction(i, 'label', e.target.value)}
                        className="h-7 text-xs"
                        disabled={!canEditAmount}
                      />
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        value={d.amount}
                        onChange={(e) => updateDeduction(i, 'amount', e.target.value)}
                        className="h-7 text-xs text-right"
                        disabled={!canEditAmount}
                      />
                      {canEditAmount && (
                        <Button size="icon" variant="ghost" className="h-6 w-6 text-red-500 hover:text-red-700" onClick={() => removeDeduction(i)}>
                          <Trash size={12} />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Net */}
                <div className="p-2 border-2 border-[#655dc6] rounded bg-[#F2F0FA] flex justify-between items-center">
                  <span className="font-bold text-[#655dc6] text-sm">
                    {T('صافي المستحقات', 'Net Payable')}
                  </span>
                  <span
                    className="text-lg font-extrabold text-[#655dc6]"
                    data-testid="clearance-net-payable"
                  >
                    {fmtSar(displayNet)} SAR
                  </span>
                </div>
                {/*
                  Stale-value banner — shown when the previously-saved
                  `final_net` was computed under the OLD FIFO-historic
                  formula and no longer matches the current-wage formula.
                  Directs the admin to re-save so the list column syncs.
                  Hidden once the doc is locked (pending_employee_signature)
                  because "Save Changes" is no longer available.
                */}
                {staleSavedFinal && active.status !== 'pending_employee_signature' && (
                  <div
                    className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800"
                    data-testid="stale-clearance-warning"
                  >
                    {(() => {
                      // Match the label of the primary action button so
                      // the reviewer knows exactly which button refreshes
                      // the stored amount. Names differ per stage:
                      //   pending_admin   → "اعتماد وإرسال للمدير المالي"
                      //   pending_finance → "اعتماد وإرسال للموظف للتوقيع"
                      //   pending_gm      → "اعتماد وإرسال للموظف للتوقيع"
                      const btnAr = active.status === 'pending_admin'
                        ? 'اعتماد وإرسال للمدير المالي'
                        : 'اعتماد وإرسال للموظف للتوقيع';
                      const btnEn = active.status === 'pending_admin'
                        ? 'Approve & Forward to Finance'
                        : 'Approve & Send for Signature';
                      return T(
                        `المبلغ المحفوظ سابقاً (${active.final_net.toLocaleString()} ر.س) محسوب بصيغة قديمة. اضغط "${btnAr}" لتحديثه إلى القيمة الحالية.`,
                        `Previously-saved amount (${active.final_net.toLocaleString()} SAR) uses the legacy FIFO formula. Click "${btnEn}" to update it to the current value.`
                      );
                    })()}
                  </div>
                )}

                {/* Admin notes */}
                <div>
                  <Label className="text-[11px]">{T('ملاحظات الإدارة', 'Admin Notes')}</Label>
                  <Textarea
                    rows={2}
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    disabled={!canSetDays}
                    placeholder={T('ملاحظات اختيارية ستظهر في PDF المخالصة', 'Optional notes shown on the PDF')}
                    className="text-xs resize-none"
                  />
                </div>

                {/* Signing display name */}
                <div>
                  <Label className="text-[11px]">{T('اسم الموقّع عن المنشأة', 'Employer Signing Name')}</Label>
                  <Input
                    value={signingName}
                    onChange={(e) => setSigningName(e.target.value)}
                    disabled={!canSetDays}
                    placeholder={T('سيظهر في خانة توقيع المنشأة على PDF', 'Will appear under Employer Signature on PDF')}
                    className="h-8 text-xs"
                    data-testid="signing-name-input"
                  />
                </div>

                {active.status === 'signed' && active.signature_name && (
                  <div className="p-1.5 bg-emerald-50 border border-emerald-200 rounded text-[11px] text-emerald-800">
                    <strong>{T('تم التوقيع بواسطة:', 'Signed by:')}</strong> {active.signature_name} · {active.signed_at ? new Date(active.signed_at).toLocaleString('en-GB', { timeZone: 'Asia/Riyadh', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }).replace(',', '') : ''}
                  </div>
                )}
              </div>
            )}
            </div>

            <DialogFooter className="gap-2 pt-2 pb-4 px-6 shrink-0 border-t border-[#C4BFF0]/40 bg-white">
              <Button variant="outline" size="sm" onClick={() => setActive(null)} disabled={submitting}>
                {T('إغلاق', 'Close')}
              </Button>
              {active && canReject && (
                <Button variant="outline" size="sm" onClick={rejectItem} disabled={submitting}
                  className="border-red-400 text-red-600 hover:bg-red-50 hover:text-red-700 gap-1">
                  <XCircle size={14} />
                  {T('رفض', 'Reject')}
                </Button>
              )}
              {active && canSetDays && (
                <Button size="sm" onClick={submitReview} disabled={submitting}
                  className="bg-[#655dc6] hover:bg-[#544cb0] gap-1" data-testid="finance-review-btn">
                  <CheckCircle size={14} />
                  {submitting
                    ? '...'
                    : (active.status === 'pending_admin'
                        ? T('اعتماد وإرسال للمدير المالي', 'Approve & Forward to Finance')
                        : active.status === 'pending_finance'
                        ? T('اعتماد وإرسال للموظف للتوقيع', 'Approve & Send for Signature')
                        : active.status === 'pending_employee_signature'
                        ? T('حفظ التعديلات', 'Save Changes')
                        : T('اعتماد وإرسال للموظف للتوقيع', 'Approve & Send for Signature'))}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete-clearance confirmation dialog — requires a written reason
            (5–500 chars) that the backend archives to `clearance_deletions`
            for legal / audit traceability. Restricted UI-side to admin/GM
            but the backend re-validates. */}
        <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) { setDeleteTarget(null); setDeleteReason(''); } }}>
          <DialogContent className="sm:max-w-[500px]" dir={direction}>
            <DialogHeader>
              <DialogTitle className="text-red-700 flex items-center gap-2">
                <Trash size={18} weight="duotone" />
                {T('حذف المخالصة نهائياً', 'Delete clearance permanently')}
              </DialogTitle>
              <DialogDescription className="text-red-800/80 leading-relaxed">
                {T(
                  'هذا الإجراء لا يمكن التراجع عنه. سيُحفظ نسخة كاملة من المخالصة في سجل تدقيق الحذف لأغراض قانونية، ولكن السجل الأصلي لن يعود للظهور. اكتب سبب الحذف بوضوح.',
                  'This action cannot be undone. A full snapshot is archived to the deletion audit log for legal purposes, but the original record will not reappear. State the reason clearly.',
                )}
              </DialogDescription>
            </DialogHeader>
            {deleteTarget && (
              <div className="text-xs bg-red-50 border border-red-200 rounded p-2 space-y-1">
                <div><span className="font-semibold">{T('الموظف:', 'Employee:')}</span> {deleteTarget.preview?.employee_name || '—'}</div>
                <div><span className="font-semibold">{T('الحالة:', 'Status:')}</span> {deleteTarget.status}</div>
                <div><span className="font-semibold">{T('صافي المستحقات:', 'Net Payable:')}</span> {(() => {
                  const v = deleteTarget.locked_final_net ?? deleteTarget.final_net;
                  return v != null ? `${fmtSar(v)} SAR` : '—';
                })()}</div>
              </div>
            )}
            <div className="space-y-1">
              <label className="text-xs font-medium text-[#655dc6]">
                {T('سبب الحذف (5–500 حرف) *', 'Reason for deletion (5–500 chars) *')}
              </label>
              <Textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                rows={4}
                maxLength={500}
                placeholder={T('اكتب سبب الحذف بوضوح — سيُسجَّل في سجل التدقيق', 'State the reason clearly — recorded in the audit log')}
                className="text-sm resize-none"
                data-testid="delete-reason-input"
              />
              <p className="text-[10px] text-[#655dc6]/60 text-end">{deleteReason.length}/500</p>
            </div>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => { setDeleteTarget(null); setDeleteReason(''); }}
                disabled={deleting}
                data-testid="delete-cancel-btn"
              >
                {T('إلغاء', 'Cancel')}
              </Button>
              <Button
                onClick={async () => {
                  const reason = deleteReason.trim();
                  if (reason.length < 5) {
                    toast.error(T('سبب الحذف قصير جداً (5 أحرف على الأقل)', 'Reason is too short (5 chars min)'));
                    return;
                  }
                  setDeleting(true);
                  try {
                    await axios.delete(`${API}/clearance/${deleteTarget.id}`, {
                      withCredentials: true,
                      data: { reason },
                    });
                    toast.success(T('تم حذف المخالصة', 'Clearance deleted'));
                    setDeleteTarget(null);
                    setDeleteReason('');
                    fetchAll();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || T('تعذّر الحذف', 'Delete failed'));
                  } finally {
                    setDeleting(false);
                  }
                }}
                disabled={deleting || deleteReason.trim().length < 5}
                className="bg-red-600 hover:bg-red-700 gap-1"
                data-testid="delete-confirm-btn"
              >
                <Trash size={14} weight="bold" />
                {deleting ? T('جارٍ الحذف…', 'Deleting…') : T('حذف نهائياً', 'Delete permanently')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
    </>
  );

  if (embed) return <div className="space-y-6">{body}</div>;

  return (
    <div className="min-h-screen bg-[#F2F0FA]" dir={direction} data-testid="admin-clearance-page">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm shadow-sm border-b border-[#C4BFF0] py-4">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <img src={LOGO_URL} alt="Lucent" className="h-10 object-contain" />
          <BackButton className="text-[#655dc6]" />
        </div>
      </header>
      <main className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div className="flex items-center gap-3">
          <Receipt size={28} weight="duotone" className="text-[#655dc6]" />
          <h1 className="text-2xl font-bold text-[#655dc6]">{T('مخالصات بدل الإجازة', 'Leave Allowance Clearances')}</h1>
        </div>
        {body}
      </main>
    </div>
  );
}
